var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useRef } from 'react';
import { removeSubstring } from 'ziphy-web-shared/basic/helpers';
import TextBlockDomHelpers from '@nedoShared/modules/notes/block/textBlock/helpers/TextBlock.dom.helpers';
import TextBlockConstants from '@nedoShared/modules/notes/block/textBlock/TextBlock.constants';
import { ITextBlockDataAttr, } from '@nedoShared/modules/notes/block/textBlock/TextBlock.types';
const { EMPTY_TEXT_CODE, SLASH, TEXT_UPDATE_DELAY } = TextBlockConstants;
const { getCaretPosition, setCaretPosition, getSelection, getSelectionOnly, getTextElement, getPlaceholderElement, focusElement, alertWithConsoleLogging, observeTextChanges, } = TextBlockDomHelpers;
const fireCaretJumpEvent = (id, moveTo) => {
    window.dispatchEvent(new CustomEvent(`TextBlockCaretJump-${id}`, {
        detail: { moveTo: moveTo },
    }));
};
export const useTextBlockKeydown = (containerRef, $store, isEnabledInputAdder = true, onRemove, id, onChangePlaceholderText) => {
    const placeholderTextTimeout = useRef({});
    const updatePlaceholderTextValue = useCallback((elementId) => {
        const element = $store.elementsMap[elementId];
        clearTimeout(placeholderTextTimeout.current[element.macroData.id]);
        const update = () => __awaiter(void 0, void 0, void 0, function* () {
            const isUpdated = yield onChangePlaceholderText(element.macroData.id, element.macroData.value);
            if (!isUpdated) {
                clearTimeout(placeholderTextTimeout.current[element.macroData.id]);
                placeholderTextTimeout.current[element.macroData.id] = setTimeout(update, 50);
            }
        });
        placeholderTextTimeout.current[element.macroData.id] = setTimeout(update, TEXT_UPDATE_DELAY);
    }, []);
    const onBackspaceOrDeleteKeydown = useCallback((e, caretInfo, selection) => {
        if (selection.startNode !== selection.endNode) {
            alertWithConsoleLogging('onBackspaceOrDeleteKeydown - selection: ', selection);
            return;
        }
        if (selection.endOffset === selection.startOffset) {
            if ($store.elements.length === 1 &&
                !$store.elements[0].value &&
                (e.key === 'Backspace' || e.key === 'Delete')) {
                onRemove(e.key);
                return;
            }
            // INFO Single deletion
            const textContent = selection.startNode.textContent;
            if (textContent === EMPTY_TEXT_CODE ||
                (e.key === 'Backspace' && selection.startOffset === 0) ||
                (e.key === 'Delete' && selection.startOffset === textContent.length)) {
                const neighborSiblingKey = e.key === 'Backspace' ? 'previousSibling' : 'nextSibling';
                const neighborSibling = caretInfo.element.element[neighborSiblingKey];
                const neighborPlaceholder = getPlaceholderElement(neighborSibling, containerRef.current);
                if (neighborPlaceholder) {
                    const activeElement = $store.deletePlaceholder(neighborPlaceholder.id);
                    focusElement(containerRef.current, activeElement);
                    return;
                }
            }
            else if (e.key === 'Backspace') {
                const newTextContent = removeSubstring(selection.startNode.textContent, selection.startOffset - 1, selection.endOffset);
                selection.startNode.textContent = newTextContent ? newTextContent : EMPTY_TEXT_CODE;
                setCaretPosition(selection.startNode, newTextContent ? selection.startOffset - 1 : 1);
            }
            else {
                const newTextContent = removeSubstring(selection.startNode.textContent, selection.startOffset, selection.endOffset + 1);
                selection.startNode.textContent = newTextContent ? newTextContent : EMPTY_TEXT_CODE;
                setCaretPosition(selection.startNode, selection.startOffset);
            }
        }
        else {
            // INFO Multi Selection
            const newTextContent = removeSubstring(selection.startNode.textContent, selection.startOffset, selection.endOffset);
            selection.startNode.textContent = newTextContent ? newTextContent : EMPTY_TEXT_CODE;
            setCaretPosition(selection.startNode, selection.startOffset);
        }
        containerRef.current.dispatchEvent(new Event('input', { bubbles: true }));
    }, [onRemove]);
    const replaceElements = useCallback((selection, replaceValue) => {
        let startTextNode = getTextElement(selection.startNode, containerRef.current);
        let endTextNode = getTextElement(selection.endNode, containerRef.current);
        if (startTextNode && endTextNode) {
            selection.originalSelection.removeAllRanges();
            const activeElement = $store.deleteElements({ elementId: startTextNode.id, offset: selection.startOffset }, { elementId: endTextNode.id, offset: selection.endOffset }, replaceValue, isEnabledInputAdder);
            focusElement(containerRef.current, activeElement);
        }
        else {
            alertWithConsoleLogging('replaceElements - selection ', selection);
        }
    }, [isEnabledInputAdder]);
    const onPaste = useCallback((event, selection) => {
        const clipboardText = event.clipboardData.getData('text');
        if (selection.startNode === selection.endNode) {
            const textElement = getTextElement(selection.startNode, containerRef.current);
            const element = $store.elementsMap[textElement === null || textElement === void 0 ? void 0 : textElement.id];
            if (element) {
                const firstHalf = element.value.slice(0, selection.startOffset) + clipboardText;
                const secondHalf = element.value.slice(selection.endOffset);
                $store.EDIT_TEXT_ELEMENT(element.id, { value: firstHalf + secondHalf });
                setTimeout(() => setCaretPosition(selection.startNode, firstHalf.length), 0);
            }
        }
        else {
            replaceElements(selection, clipboardText);
        }
    }, [replaceElements]);
    const onCutCopy = useCallback((key, selection) => {
        let startTextNode = getTextElement(selection.startNode, containerRef.current);
        let endTextNode = getTextElement(selection.endNode, containerRef.current);
        if (startTextNode && endTextNode && startTextNode === endTextNode) {
            navigator.clipboard
                .writeText(startTextNode.textContent.slice(selection.startOffset, selection.endOffset))
                .finally(() => {
                if (key === 'x') {
                    const element = $store.elementsMap[startTextNode === null || startTextNode === void 0 ? void 0 : startTextNode.id];
                    const value = removeSubstring(element.value, selection.startOffset, selection.endOffset);
                    $store.EDIT_TEXT_ELEMENT(element.id, { value });
                    setTimeout(() => setCaretPosition(startTextNode, selection.startOffset), 0);
                }
            });
        }
        else if (startTextNode && endTextNode) {
            navigator.clipboard
                .writeText($store.copyElements({ elementId: startTextNode.id, offset: selection.startOffset }, { elementId: endTextNode.id, offset: selection.endOffset }))
                .finally(() => {
                if (key === 'x') {
                    const activeElement = $store.deleteElements({ elementId: startTextNode.id, offset: selection.startOffset }, { elementId: endTextNode.id, offset: selection.endOffset }, '');
                    focusElement(containerRef.current, activeElement);
                }
            });
        }
    }, []);
    useEffect(() => {
        var _a, _b, _c;
        const paste = (event) => {
            event.preventDefault();
            const initialSelection = getSelectionOnly();
            if (initialSelection.startNode === initialSelection.endNode) {
                const placeholder = getPlaceholderElement(initialSelection.startNode, containerRef.current);
                if (placeholder) {
                    if (!placeholder.firstChild.isContentEditable) {
                        return;
                    }
                    const clipboardText = event.clipboardData.getData('text');
                    const element = $store.elementsMap[placeholder.id];
                    const value = element.macroData.value || '';
                    const newValue = `${value.slice(0, initialSelection.startOffset)}${clipboardText}${value.slice(initialSelection.endOffset)}`;
                    $store.EDIT_PLACEHOLDER_ELEMENT(element.id, { value: newValue });
                    updatePlaceholderTextValue(element.id);
                    observeTextChanges(placeholder.firstChild, (observer) => {
                        setCaretPosition(placeholder.firstChild, initialSelection.startOffset + clipboardText.length);
                        observer.disconnect();
                    });
                    return;
                }
            }
            const selection = getSelection(containerRef.current);
            if (!selection) {
                alertWithConsoleLogging('No selection found.');
                return;
            }
            onPaste(event, selection);
        };
        const cut = (event) => {
            event.preventDefault();
            const initialSelection = getSelectionOnly();
            if (initialSelection.startNode === initialSelection.endNode) {
                const placeholder = getPlaceholderElement(initialSelection.startNode, containerRef.current);
                if (placeholder) {
                    if (!placeholder.firstChild.isContentEditable) {
                        return;
                    }
                    const clipboardText = initialSelection.originalSelection.toString();
                    const element = $store.elementsMap[placeholder.id];
                    const newValue = removeSubstring(element.macroData.value, initialSelection.startOffset, initialSelection.endOffset);
                    $store.EDIT_PLACEHOLDER_ELEMENT(element.id, { value: newValue });
                    updatePlaceholderTextValue(element.id);
                    observeTextChanges(placeholder.firstChild, (observer) => {
                        setCaretPosition(placeholder.firstChild, initialSelection.startOffset);
                        observer.disconnect();
                    });
                    navigator.clipboard.writeText(clipboardText);
                    return;
                }
            }
            const selection = getSelection(containerRef.current);
            if (!selection) {
                alertWithConsoleLogging('No selection found.');
                return;
            }
            onCutCopy('x', selection);
        };
        const copy = (event) => {
            event.preventDefault();
            const initialSelection = getSelectionOnly();
            if (initialSelection.startNode === initialSelection.endNode) {
                const placeholder = getPlaceholderElement(initialSelection.startNode, containerRef.current);
                if (placeholder) {
                    if (!placeholder.firstChild.isContentEditable) {
                        return;
                    }
                    navigator.clipboard.writeText(initialSelection.originalSelection.toString());
                    return;
                }
            }
            const selection = getSelection(containerRef.current);
            if (!selection) {
                alertWithConsoleLogging('No selection found.');
                return;
            }
            onCutCopy('c', selection);
        };
        (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener('paste', paste);
        (_b = containerRef.current) === null || _b === void 0 ? void 0 : _b.addEventListener('cut', cut);
        (_c = containerRef.current) === null || _c === void 0 ? void 0 : _c.addEventListener('copy', copy);
        return () => {
            var _a, _b, _c;
            (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('paste', paste);
            (_b = containerRef.current) === null || _b === void 0 ? void 0 : _b.removeEventListener('cut', cut);
            (_c = containerRef.current) === null || _c === void 0 ? void 0 : _c.removeEventListener('copy', copy);
        };
    }, []);
    const onEnter = useCallback((e, caretInfo, selection) => {
        e.preventDefault();
        const element = $store.elementsMap[caretInfo.element.element.id];
        const value = `${element.value.slice(0, selection.startOffset)}\n${element.value.slice(selection.endOffset)}`;
        $store.EDIT_TEXT_ELEMENT(element.id, { value });
        observeTextChanges(caretInfo.element.element, (observer) => {
            setCaretPosition(caretInfo.element.element, selection.startOffset + 1);
            observer.disconnect();
        });
    }, []);
    const onTextKeydown = useCallback((e, caretInfo) => {
        const isEmptyNode = caretInfo.node.textContent === EMPTY_TEXT_CODE;
        if (!e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight') && isEmptyNode) {
            e.preventDefault();
            let neighborSiblingKey = e.key === 'ArrowLeft' ? 'previousSibling' : 'nextSibling';
            let neighborSibling = caretInfo.element.element[neighborSiblingKey];
            while (neighborSibling) {
                const textElement = getTextElement(neighborSibling, containerRef.current);
                if (textElement) {
                    const offset = e.key === 'ArrowLeft' ? textElement.textContent.length : 0;
                    setCaretPosition(textElement, offset);
                    return;
                }
                neighborSibling = neighborSibling[neighborSiblingKey];
            }
            fireCaretJumpEvent(id, e.key === 'ArrowLeft' ? 'top' : 'bottom');
            return;
        }
        const selection = getSelection(containerRef.current);
        if (!selection) {
            alertWithConsoleLogging('No selection found.');
            return;
        }
        // MAIN ACTIONS
        if (e.shiftKey) {
            if (e.key === 'Enter') {
                e.preventDefault();
                if (selection.startNode !== selection.endNode) {
                    replaceElements(selection, '\n');
                }
                else {
                    onEnter(e, caretInfo, selection);
                }
            }
            return;
        }
        else if (e.ctrlKey || e.metaKey) {
            // Copy, Paste, Select All, etc.
            return;
        }
        else if (selection.startNode !== selection.endNode) {
            // Multi deletion (when selected placeholders)
            let value = '';
            let doReplace = true;
            if (e.key === 'Backspace' || e.key === 'Delete') {
                e.preventDefault();
            }
            else if (e.key === 'Enter') {
                e.preventDefault();
                value = '\n';
            }
            else if (e.key.length === 1) {
                value = e.key;
            }
            else {
                doReplace = false;
            }
            if (doReplace) {
                replaceElements(selection, value);
            }
        }
        else if (e.key === 'Backspace' || e.key === 'Delete') {
            // Single deletion (common behavior)
            e.preventDefault();
            onBackspaceOrDeleteKeydown(e, caretInfo, selection);
        }
        else if (e.key === 'Enter') {
            e.preventDefault();
            onEnter(e, caretInfo, selection);
        }
        else if (e.key === SLASH && isEnabledInputAdder) {
            const textElement = getTextElement(selection.startNode, containerRef.current);
            if (textElement) {
                e.preventDefault();
                const activeElement = $store.createInput(textElement.id, selection);
                focusElement(containerRef.current, activeElement);
            }
        }
        else if ((e.key === 'ArrowUp' || e.key === 'ArrowLeft') &&
            selection.startNode.id === $store.elements[0].id &&
            selection.startOffset === 0) {
            e.preventDefault();
            fireCaretJumpEvent(id, 'top');
        }
        else if ((e.key === 'ArrowDown' || e.key === 'ArrowRight') &&
            selection.endNode.id === $store.elements[$store.elements.length - 1].id &&
            selection.endOffset === $store.elements[$store.elements.length - 1].value.length) {
            e.preventDefault();
            fireCaretJumpEvent(id, 'bottom');
        }
    }, [onBackspaceOrDeleteKeydown, id]);
    const onInputKeydown = useCallback((e, elementId, caretInfo) => {
        if (caretInfo.offset === 0) {
            e.preventDefault();
            setCaretPosition(caretInfo.element.element, 1);
        }
        else if (e.key === 'ArrowUp' ||
            e.key === 'ArrowDown' ||
            e.key === 'Enter' ||
            (caretInfo.offset === 1 && e.key === 'ArrowLeft') ||
            (caretInfo.offset === caretInfo.node.textContent.length && e.key === 'ArrowRight')) {
            e.preventDefault();
        }
        else if (e.key === 'Backspace') {
            const isEmptyContent = caretInfo.offset === 1 && caretInfo.node.textContent === SLASH;
            if (isEmptyContent) {
                e.preventDefault();
                focusElement(containerRef.current, $store.deleteInput(elementId));
            }
        }
        else if (e.key === ' ' || e.code === 'Space') {
            e.preventDefault();
            focusElement(containerRef.current, $store.mergeInput(elementId));
        }
    }, []);
    const onPlaceholderTextKeydown = useCallback((e, caretInfo) => {
        const selection = getSelectionOnly();
        if (!selection) {
            alertWithConsoleLogging('No selection found.');
            return;
        }
        else if (selection.startNode !== selection.endNode) {
            alertWithConsoleLogging('onPlaceholderTextKeydown: startNode !== endNode');
            return;
        }
        else if (!caretInfo.element.element.firstChild.isContentEditable) {
            return;
        }
        const element = $store.elementsMap[caretInfo.element.element.id];
        const value = element.macroData.value || '';
        const textContent = caretInfo.element.element.firstChild.textContent;
        const emptyTextCodeIndex = textContent.indexOf(EMPTY_TEXT_CODE);
        const maxLength = emptyTextCodeIndex > -1 ? emptyTextCodeIndex : textContent.length;
        if (e.ctrlKey || e.metaKey) {
            // Copy, Paste, Select All, etc.
            return;
        }
        else if (e.shiftKey) {
            // Selection by Shift + ArrowLeft/ArrowRight
            if ((e.key === 'ArrowLeft' && selection.startOffset > 0) ||
                (e.key === 'ArrowRight' && selection.endOffset < maxLength)) {
                return;
            }
            else if (e.key.length > 1) {
                e.preventDefault();
                return;
            }
        }
        e.preventDefault();
        if (e.key === 'Enter') {
            const newValue = `${value.slice(0, selection.startOffset)}\n${value.slice(selection.endOffset)}`;
            $store.EDIT_PLACEHOLDER_ELEMENT(element.id, { value: newValue });
            updatePlaceholderTextValue(element.id);
            observeTextChanges(caretInfo.element.element.firstChild, (observer) => {
                setCaretPosition(caretInfo.element.element.firstChild, selection.startOffset + 1);
                observer.disconnect();
            });
        }
        else if (e.key === 'Backspace') {
            if (selection.startOffset > 0 && selection.startOffset === selection.endOffset) {
                const newValue = `${value.slice(0, selection.startOffset - 1)}${value.slice(selection.endOffset)}`;
                $store.EDIT_PLACEHOLDER_ELEMENT(element.id, { value: newValue });
                updatePlaceholderTextValue(element.id);
                observeTextChanges(caretInfo.element.element.firstChild, (observer) => {
                    setCaretPosition(caretInfo.element.element.firstChild, selection.startOffset - 1);
                    observer.disconnect();
                });
            }
            else if (selection.startOffset < selection.endOffset) {
                const newValue = `${value.slice(0, selection.startOffset)}${value.slice(selection.endOffset)}`;
                $store.EDIT_PLACEHOLDER_ELEMENT(element.id, { value: newValue });
                updatePlaceholderTextValue(element.id);
                observeTextChanges(caretInfo.element.element.firstChild, (observer) => {
                    setCaretPosition(caretInfo.element.element.firstChild, selection.startOffset);
                    observer.disconnect();
                });
            }
        }
        else if (e.key === 'Delete') {
            if (selection.startOffset < maxLength && selection.startOffset === selection.endOffset) {
                const newValue = `${value.slice(0, selection.startOffset)}${value.slice(selection.endOffset + 1)}`;
                $store.EDIT_PLACEHOLDER_ELEMENT(element.id, { value: newValue });
                updatePlaceholderTextValue(element.id);
                observeTextChanges(caretInfo.element.element.firstChild, (observer) => {
                    setCaretPosition(caretInfo.element.element.firstChild, selection.startOffset);
                    observer.disconnect();
                });
            }
            else if (selection.startOffset < selection.endOffset) {
                const newValue = `${value.slice(0, selection.startOffset)}${value.slice(selection.endOffset)}`;
                $store.EDIT_PLACEHOLDER_ELEMENT(element.id, { value: newValue });
                updatePlaceholderTextValue(element.id);
                observeTextChanges(caretInfo.element.element.firstChild, (observer) => {
                    setCaretPosition(caretInfo.element.element.firstChild, selection.startOffset);
                    observer.disconnect();
                });
            }
        }
        else if (e.key === 'ArrowLeft') {
            if (selection.startOffset < selection.endOffset) {
                setCaretPosition(caretInfo.element.element.firstChild, selection.startOffset);
            }
            else if (selection.startOffset > 0) {
                setCaretPosition(caretInfo.element.element.firstChild, selection.startOffset - 1);
            }
        }
        else if (e.key === 'ArrowRight') {
            if (selection.startOffset < selection.endOffset) {
                setCaretPosition(caretInfo.element.element.firstChild, selection.endOffset);
            }
            else if (selection.endOffset < maxLength) {
                setCaretPosition(caretInfo.element.element.firstChild, selection.endOffset + 1);
            }
        }
        else if (e.key.length === 1) {
            const newValue = `${value.slice(0, selection.startOffset)}${e.key}${value.slice(selection.endOffset)}`;
            $store.EDIT_PLACEHOLDER_ELEMENT(element.id, { value: newValue });
            updatePlaceholderTextValue(element.id);
            observeTextChanges(caretInfo.element.element.firstChild, (observer) => {
                setCaretPosition(caretInfo.element.element.firstChild, selection.startOffset + 1);
                observer.disconnect();
            });
        }
    }, []);
    return useCallback((e) => {
        const caretInfo = getCaretPosition(containerRef.current);
        if (caretInfo.element && caretInfo.element.type === ITextBlockDataAttr.INPUT) {
            onInputKeydown(e, caretInfo.element.element.getAttribute('id'), caretInfo);
        }
        else if (caretInfo.element && caretInfo.element.type === ITextBlockDataAttr.TEXT) {
            onTextKeydown(e, caretInfo);
        }
        else if (caretInfo.element && caretInfo.element.type === ITextBlockDataAttr.PLACEHOLDER) {
            onPlaceholderTextKeydown(e, caretInfo);
        }
        else {
            //  alertWithConsoleLogging('useTextBlockKeydown: not found caretInfo', caretInfo)
        }
    }, [onTextKeydown, onInputKeydown]);
};
